import { Injectable } from '@angular/core';
import * as R from 'ramda';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { concatMap, filter, first, map, mergeMap, shareReplay, switchMap, tap, toArray } from 'rxjs/operators';

import { APIService, KPI } from '../../@core/api';
import { AssetsService } from './assets.service';
import { InvestmentsService } from './investments.service';
import { ScopeService } from './scope.service';
import { UsersService } from './users.service';

@Injectable()
export class TasksService {

  private _userTasks$: Observable<KPI.MappedTask[]>;
  private _taskStates$: Observable<KPI.Display<KPI.TaskState>[]>;
  private userTasksRefresh$ = new BehaviorSubject<void>(null);

  constructor(
    private api: APIService,
    private assetsService: AssetsService,
    private investmentsService: InvestmentsService,
    private scope: ScopeService,
    private users: UsersService,
  ) { }

  public get userTasks$(): Observable<KPI.MappedTask[]> {
    if (!this._userTasks$) {
      this._userTasks$ = combineLatest(
        this.scope.user$,
        this.userTasksRefresh$,
      ).pipe(
        switchMap(([user]) => this.getUserTasks(user)),
        shareReplay(1),
      );
    }

    return this._userTasks$;
  }

  public refreshUserTasks(): void {
    this.userTasksRefresh$.next();
  }

  public get taskStates$(): Observable<KPI.Display<KPI.TaskState>[]> {
    if (!this._taskStates$) {
      this._taskStates$ = this.api.tasks_task_states().pipe(shareReplay(1));
    }

    return this._taskStates$;
  }

  public getUserTasks(user: KPI.User): Observable<KPI.MappedTask[]> {
    return this.api.tasks_tasks({ assigned: user.get_user_id }).pipe(
      concatMap((tasks) => tasks),
      concatMap((task) => this.mapTask(task)),
      toArray(),
      map((tasks) => R.sortWith(
        [
          R.descend(R.prop('done')),
          R.ascend(R.prop('deadline_date')),
        ],
        tasks,
      )),
    );
  }

  public getInvestmentTasks(investment: KPI.Investment): Observable<KPI.MappedTask[]> {
    return this.api.tasks_tasks({ investments: investment.id }).pipe(
      concatMap((tasks) => tasks),
      concatMap((task) => this.mapTask(task)),
      toArray(),
      map((tasks) => R.sortWith(
        [
          R.descend(R.prop('done')),
          R.ascend(R.prop('deadline_date')),
        ],
        tasks,
      )),
    );
  }

  /**
   * Get all tasks linked to an asset
   *
   * @param asset
   */
  public getAssetTasks(asset: KPI.Asset): Observable<KPI.MappedTask[]> {
    return this.api.tasks_tasks({ assets: asset.id }).pipe(
      concatMap((tasks) => tasks),
      concatMap((task) => this.mapTask(task)),
      toArray(),
      map((tasks) => R.sortWith(
        [
          R.descend(R.prop('done')),
          R.ascend(R.prop('deadline_date')),
        ],
        tasks,
      )),
    );
  }

  public createTask(task: Partial<KPI.Task>): Observable<KPI.MappedTask> {
    return this.api.tasks_tasks_post(task).pipe(
      concatMap((createdTask) => this.mapTask(createdTask)),
      tap(() => this.refreshUserTasks()),
    );
  }

  public createBulkTasks(groupTask: Partial<KPI.GroupTasks>): Observable<void> {
    return this.api.tasks_tasks_bulk_post(groupTask).pipe(
      tap(() => this.refreshUserTasks()),
    );
  }

  public updateTask(id: number, task: Partial<KPI.Task>): Observable<KPI.MappedTask> {
    return this.api.tasks_tasks_patch(id, task).pipe(
      concatMap((updatedTask) => this.mapTask(updatedTask)),
      tap(() => this.refreshUserTasks()),
    );
  }

  public deleteTask(id: number): Observable<void> {
    return this.api.tasks_tasks_delete(id).pipe(
      tap(() => this.refreshUserTasks()),
    );
  }

  private mapTask(task: KPI.Task): Observable<KPI.MappedTask> {
    return combineLatest(
      this.taskStates$,
      this.users.users$,
    ).pipe(
      filter(([taskStates, users]) => taskStates.length > 0 && users.length > 0),
      first(),
      map(([taskStates, users]) => ({
        ...task,
        assigned_object: users.find((user) => user.get_user_id === task.assigned),
        created_by_object: users.find((user) => user.get_user_id === task.created_by),
        state_object: taskStates.find((taskState) => taskState.id === task.state),
        deadline_date: new Date(task.deadline),
      })),
      // Map investment if any
      mergeMap((mappedTask) =>
        R.isNil(mappedTask.investment) ? of(mappedTask) :
          this.investmentsService.getInvestment(mappedTask.investment).pipe(
            map((investment) => ({
              ...mappedTask,
              investment_object: investment,
            })),
          ),
      ),
      // Map asset if any
      mergeMap((mappedTask) =>
        R.isNil(mappedTask.asset) ? of(mappedTask) :
          this.assetsService.getAsset(mappedTask.asset).pipe(
            map((asset) => ({
              ...mappedTask,
              asset_object: asset,
            })),
          ),
      ),
    );
  }

}
